<template>
  <base-page-layout title="Classificação RFV" class="px-7">
    <template v-slot:header>
      <rfv-pdf :subtitle="getPdfSubtitle" :formatted-pdf-dates="formattedPdfDates" :loading="isLoading" />
    </template>
    <div class="filters-container mb-2">
      <analysis-configuration @change-analysis-configuration="handleSelectAnalyticConfiguration" />
      <new-filters-menu :filters="['segmentos']" />
    </div>
    <div class="period-container">
      <h2 class="mb-2">Período Analisado</h2>
      <p class="mb-4">
        O período levado em consideração pela análise é sempre um período de 3 meses, selecione o último mês desse período desejado para visualizar os dados.
      </p>
    </div>
    <div class="d-flex align-center justify-space-between mb-4">
      <month-range-picker
        ref="monthRangePicker"
        :min="minAnalyticPeriod"
        :max="maxAnalyticPeriod"
        :initial-date="currentPeriodInitialDate"
        :final-date="currentPeriodFinalDate"
        :is-dynamic-period="isDynamicPeriod"
        variant="current"
        :disabled="isNotRecurrent"
        :is-not-recurrent="isNotRecurrent"
        @set-dates="setCurrentDatesLabels"
        @input="setCurrentDatesValues"
      />
      <mf-tabs-button v-model="tab" :buttons="tabs" />
    </div>
    <mf-info-card
      v-if="isDynamicPeriod"
      class="mb-4"
      variant="info"
      description="Acompanhe a evolução dos seus grupos de clientes diariamente com análises parciais dos últimos 90 dias. A análise mensal completa continua sendo gerada no ínicio de cada mês."
    />
    <rfv-graph v-show="tab === 'graphic'" :period="currentPeriod" :loading="loadingCurrentPeriod" @more-details="tab = 'table'" />
    <rfv-segments-details-table v-show="tab === 'table'" :period="currentPeriod" :loading="loadingCurrentPeriod" />
    <comparison-between-periods
      ref="comparisonBetweenPeriods"
      :loading-current-period="loadingCurrentPeriod"
      :formatted-current-dates="formattedCurrentDates"
      :skiping-load="skippingLoad"
      @set-reference-dates-labels="setReferenceDatesLabels($event)"
      @load-migration-data="loadMigrationData"
    />
  </base-page-layout>
</template>

<script>
import RfvPdf from '../components/pdf/RfvPdf.vue'
import BasePageLayout from '@/components/BasePageLayout.vue'
import MonthRangePicker from '../components/MonthRangePicker.vue'
import ComparisonBetweenPeriods from '../components/comparison-between-periods/index.vue'
import RfvGraph from '../components/RfvGraph.vue'
import RfvSegmentsDetailsTable from '../components/RfvSegmentsDetailsTable.vue'
import NewFiltersMenu from '@/components/filters-menu/NewFiltersMenu.vue'
import AnalysisConfiguration from '../components/AnalysisConfiguration.vue'
import { rfvAnalysis } from '../../mixins/analise-rfv'
import { DateTime } from 'luxon'
import { mapState } from 'vuex'

export default {
  components: {
    BasePageLayout,
    MonthRangePicker,
    ComparisonBetweenPeriods,
    RfvGraph,
    RfvSegmentsDetailsTable,
    RfvPdf,
    NewFiltersMenu,
    AnalysisConfiguration,
  },
  mixins: [rfvAnalysis],
  data: () => ({
    isFirstTimeLoadingData: true,
    isQuickAction: false,
    analyticPeriod: {
      initialDate: '',
      finalDate: '',
    },
    tab: 'graphic',
    loadingCurrentPeriod: true,
    formattedCurrentDates: null,
    formattedReferenceDates: null,
  }),
  computed: {
    ...mapState({ stateFilters: (state) => state.filters }),
    isLoading() {
      return this.loadingCurrentPeriod || this.$refs.comparisonBetweenPeriods.loadingReferencePeriod
    },
    currentPeriodInitialDate() {
      if (!this.currentPeriod?.initialDate) return ''
      else if (this.isDynamicPeriod) return this.currentPeriod.initialDate
      return DateTime.fromFormat(this.currentPeriod.initialDate, 'yyyy-MM-dd').toFormat('yyyy-MM')
    },
    currentPeriodFinalDate() {
      if (!this.currentPeriod?.finalDate) return ''
      else if (this.isDynamicPeriod) return this.currentPeriod.finalDate
      return DateTime.fromFormat(this.currentPeriod.finalDate, 'yyyy-MM-dd').toFormat('yyyy-MM')
    },
    isDynamicPeriod() {
      return this.currentPeriod?.dynamic
    },
    tabs() {
      return [
        {
          id: 0,
          label: 'Gráfico',
          value: 'graphic',
          active: true,
        },
        {
          id: 1,
          label: 'Tabela',
          value: 'table',
          active: true,
        },
      ]
    },
    skippingLoad() {
      return this.isQuickAction && this.isFirstTimeLoadingData
    },
    getPdfSubtitle() {
      if (!this.formattedCurrentDates?.initialDate || !this.formattedCurrentDates?.finalDate) return ''
      return `${this.formattedCurrentDates.initialDate} ~ ${this.formattedCurrentDates.finalDate}`
    },
    formattedPdfDates() {
      return {
        currentDates: `${this.formattedCurrentDates?.initialDate} - ${this.formattedCurrentDates?.finalDate}`,
        referenceDates: `${this.formattedReferenceDates?.initialDate} - ${this.formattedReferenceDates?.finalDate}`,
      }
    },
    isNotRecurrent() {
      return this.analyticsConfiguration?.model === 'NOT_RECURRENT'
    },
  },
  async created() {
    if (this.$route.query.quick_action === 'true') {
      this.isQuickAction = true
    }

    const { analysisId } = this.$route?.params

    await this.getAnalyticsConfigurations({
      variables: { type: 'RFV' },
    })

    const defaultAnalysis = analysisId
      ? this.rfvAnalyticConfigurations.find((analysis) => analysis._id === analysisId)
      : this.rfvAnalyticConfigurations.find((analysis) => analysis.default)
    await this.handleSelectAnalyticConfiguration(defaultAnalysis)
    this.isFirstTimeLoadingData = false
    this.$nextTick(() => {
      this.$bus.$on('filtrar', this.proxyLoadData)
    })
  },
  destroyed() {
    this.$bus.$off('filtrar')
  },
  methods: {
    async handleSelectAnalyticConfiguration(analysis) {
      this.SET_ANALYTICS_CONFIGURATION(analysis)

      await this.getAllAnalyticPeriods({
        analyticConfigurationsIds: this.analyticsConfiguration?._id,
        removeDuplicates: true,
      })

      if (this.analyticAllPeriods?.length) {
        this.setCurrentPeriod(this.analyticAllPeriods[0])
        this.setReferencePeriod(this.analyticAllPeriods.length > 1 ? this.analyticAllPeriods[1] : this.analyticAllPeriods[0])
      }

      await this.proxyLoadData()
      await this.$refs.comparisonBetweenPeriods?.loadReferenceData()
      this.$refs.comparisonBetweenPeriods.referencePeriodMonthRangePickerKey++
    },
    async loadRfvGraph() {
      const variables = {
        analyticConfigurationId: this.analyticsConfiguration._id,
        periodId: this.currentPeriod._id,
        segments: this.stateFilters.segments,
      }
      this.loadingCurrentPeriod = true
      await this.getClientsMetrics({ variables, periodType: 'current' })
      this.loadingCurrentPeriod = false
    },
    setReferenceDatesLabels(value) {
      this.formattedReferenceDates = value
    },
    setCurrentDatesLabels(value) {
      this.formattedCurrentDates = value
    },
    setCurrentDatesValues(value) {
      const isCurrentMonth = value[1] === DateTime.fromFormat(this.maxAnalyticPeriod, 'yyyy-MM-dd').toFormat('yyyy-MM')

      const newCurrentPeriod = isCurrentMonth
        ? this.analyticAllPeriods[0]
        : this.analyticAllPeriods.find(
            (period) =>
              DateTime.fromFormat(period.initialDate, 'yyyy-MM-dd').toFormat('yyyy-MM') === value[0] &&
              DateTime.fromFormat(period.finalDate, 'yyyy-MM-dd').toFormat('yyyy-MM') === value[1]
          )

      if (newCurrentPeriod) {
        this.setCurrentPeriod(newCurrentPeriod)
        this.proxyLoadData()
      } else {
        this.$snackbar({ message: 'Não foi possível encontrar dados para o período selecionado.', snackbarColor: 'error' })
      }
    },
    async loadMigrationData() {
      const variables = {
        referenceAnalyticConfigurationId: this.analyticsConfiguration._id,
        analyzedAnalyticConfigurationId: this.analyticsConfiguration._id,
        referencePeriodId: this.referencePeriod._id,
        analyzedPeriodId: this.currentPeriod._id,
        segments: this.stateFilters.segments,
      }
      await this.getPeriodMigrations(variables)
    },
    async proxyLoadData() {
      await this.loadRfvGraph()
      await this.loadMigrationData()
    },
  },
}
</script>

<style lang="scss" scoped>
.period-container {
  max-width: 492px;
  h2 {
    font-size: 18px;
    font-weight: 700;
    color: $cinza-d10;
  }

  p {
    font-size: 14px;
    color: $cinza-0;
    line-height: 20px;
  }
}
.filters-container {
  gap: 20px;
  display: grid;
  grid-template-columns: 60% 40%;
}
</style>
